import { Icon } from "@circle-react-shared/Icon";
import { Combobox } from "@circle-react-uikit/Combobox";
import { Typography } from "@circle-react-uikit/Typography";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Options } from "@popperjs/core";
import { noop } from "lodash";
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import { t } from "@/i18n-js/instance";
import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";
import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";

import { popperSameWidthModifier } from "../Dropdown/utils";
import { defaultDisplayValue } from "./defaultDisplayValue";

export interface SearchableSelectProps<T = unknown> {
  appendTo?: HTMLElement | null;
  autoComplete?: string;
  children: ReactNode;
  className?: string;
  displayValue?: (option: T) => string;
  hideCaret?: boolean;
  inputClassName?: string;
  isError?: boolean;
  label?: ReactNode;
  onChange?: (option: T | null) => void;
  onClose?: () => void;
  onFilter?: (value: string) => void;
  placeholder?: string;
  popperOptions?: Partial<Options>;
  tooltipText?: string | null;
  value?: T | null;
}

const SearchableSelectComponent = <T,>({
  appendTo = null,
  autoComplete,
  children,
  displayValue = defaultDisplayValue,
  hideCaret = false,
  isError = false,
  label = "",
  onChange = noop,
  onClose = noop,
  onFilter = noop,
  placeholder = t("select_an_option"),
  popperOptions = {},
  tooltipText = null,
  inputClassName,
  value,
  ...rest
}: SearchableSelectProps<T>) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [targetElement, setTargetElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const { styles, attributes, forceUpdate } = usePopper(
    targetElement,
    popperElement,
    {
      placement: "bottom",
      strategy: "fixed",
      modifiers: [popperSameWidthModifier],
      ...popperOptions,
    },
  );

  const renderList = (
    <div
      className="z-10"
      ref={setPopperElement}
      style={styles.popper}
      {...attributes.popper}
    >
      <Combobox.Options onClose={onClose}>
        <Combobox.OptionsScrollableContainer>
          {children}
        </Combobox.OptionsScrollableContainer>
      </Combobox.Options>
    </div>
  );

  return (
    <Combobox {...rest} value={value} onChange={onChange}>
      {({ open }: { open: boolean }) => {
        if (open !== isOpen) {
          setIsOpen(open);

          if (open && forceUpdate) {
            // Defer to next frame so targetElement ref is committed before Popper calculates position
            requestAnimationFrame(() => {
              forceUpdate();
            });
          }
        }

        return (
          <>
            {label && (
              <Combobox.Label className="mb-2">
                <Typography.LabelSm weight="semibold">
                  {label}
                </Typography.LabelSm>
                {tooltipText && (
                  <span className="ml-2 inline-flex">
                    <TippyV2 content={tooltipText}>
                      <Icon
                        type="question-mark"
                        className={classNames(
                          anchorClassNames("icon"),
                          legacyClassNames("icon--xs"),
                        )}
                      />
                    </TippyV2>
                  </span>
                )}
              </Combobox.Label>
            )}
            <Combobox.InputWrapper ref={setTargetElement} isError={isError}>
              <Combobox.Input
                autoComplete={autoComplete}
                className={inputClassName}
                displayValue={displayValue}
                onChange={event => onFilter(event.target.value)}
                placeholder={placeholder}
                onFocus={() => {
                  if (open) return;
                  buttonRef.current?.click();
                }}
                onClick={() => {
                  if (open) return;
                  buttonRef.current?.click();
                }}
              />
              {!hideCaret && <Combobox.ChevronDownButton ref={buttonRef} />}
            </Combobox.InputWrapper>
            {!appendTo && renderList}
            {!!appendTo && createPortal(renderList, appendTo)}
          </>
        );
      }}
    </Combobox>
  );
};

export const SearchableSelect = Object.assign(SearchableSelectComponent, {
  EmptyOption: Combobox.EmptyOption,
  Option: Combobox.Option,
});
